import { Component, OnInit } from '@angular/core';
import {
  RegistrationDbLogsExportQuery,
  RegistrationDbLogsQuery,
  RegistrationDbService,
  RegistrationDbUpdateLogRow,
} from 'src/app/services/registration-db.service';
import {
  TableSettings,
  TableSpacing,
} from 'src/app/components/pagination/pagination.component';

@Component({
  selector: 'app-registration-db-logs-page',
  templateUrl: './registration-db-logs-page.component.html',
  styleUrl: './registration-db-logs-page.component.scss',
})
export class RegistrationDbLogsPageComponent implements OnInit {
  rows: RegistrationDbUpdateLogRow[] = [];
  loading = false;
  error = '';

  accountNumber = '';
  createdBy = '';
  createdAfter = '';
  createdBefore = '';

  pageSize = 20;
  currentPage = 1;
  totalCount = 0;
  tableSpacing: TableSpacing = 'normal';
  exporting = false;

  private searchQuery: RegistrationDbLogsExportQuery = {};

  constructor(private registrationDbService: RegistrationDbService) {}

  ngOnInit() {
    this.initializeDateFilters();
    this.loadSettings();
    this.fetch();
  }

  private initializeDateFilters() {
    const today = new Date();
    const previousMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      Math.min(
        today.getDate(),
        new Date(today.getFullYear(), today.getMonth(), 0).getDate()
      )
    );
    this.createdAfter = this.formatDateInput(previousMonth);
    this.createdBefore = this.formatDateInput(today);
  }

  private formatDateInput(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get totalPages() {
    if (this.totalCount === 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  loadSettings() {
    const raw = localStorage.getItem('registrationDbLogsTableSettings');
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw) as TableSettings;
      this.pageSize = parsed.pageSize;
      this.tableSpacing = parsed.spacing;
    } catch {
      /* ignore */
    }
  }

  saveSettings(settings: TableSettings) {
    const sizeChanged = settings.pageSize !== this.pageSize;
    this.pageSize = settings.pageSize;
    this.tableSpacing = settings.spacing;
    localStorage.setItem(
      'registrationDbLogsTableSettings',
      JSON.stringify(settings)
    );
    if (sizeChanged) {
      this.currentPage = 1;
    }
  }

  search() {
    this.currentPage = 1;
    this.fetch();
  }

  clearFilters() {
    this.accountNumber = '';
    this.createdBy = '';
    this.createdAfter = '';
    this.createdBefore = '';
    this.currentPage = 1;
    this.fetch();
  }

  fetch() {
    this.loading = true;
    this.error = '';
    this.searchQuery = {
      accountNumber: this.accountNumber || undefined,
      createdBy: this.createdBy || undefined,
      createdAfter: this.createdAfter || undefined,
      createdBefore: this.createdBefore || undefined,
    };
    const q: RegistrationDbLogsQuery = {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      ...this.searchQuery,
    };
    this.registrationDbService.getUpdateLogs(q).subscribe({
      next: (res) => {
        this.loading = false;
        this.rows = res.rows;
        this.totalCount = res.total_count;
      },
      error: () => {
        this.loading = false;
        this.error = 'Could not load registration database logs.';
      },
    });
  }

  onPageChanged(page: number) {
    this.currentPage = page;
    this.fetch();
  }

  exportCsv() {
    if (this.exporting || this.totalCount === 0) {
      return;
    }
    this.exporting = true;
    this.error = '';
    this.registrationDbService.exportUpdateLogs(this.searchQuery).subscribe({
      next: (blob) => {
        this.exporting = false;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const today = new Date().toISOString().slice(0, 10);
        link.href = url;
        link.download = `registration-db-logs-${today}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.exporting = false;
        this.error = 'Could not export registration database logs.';
      },
    });
  }
}
